import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useClient } from '../../../../hooks/useClient';
import { useNavigateScroll } from '../../../../hooks/useNavigateScroll';
import { useResourcePanel } from '../../../../hooks/useResourcePanel';
import { Routine } from '../../../../types/Fisiofit/routine';
import { ProfileOrganization, RoutineHistoryItem } from '../types';
import { useProfileClient } from './useProfileClient';

export const useProfileRoutinePanel = ({
  organization,
  externalUserId,
  user,
  setActiveTabIndex,
}: {
  organization: ProfileOrganization;
  externalUserId: string | undefined;
  user: ReturnType<typeof useProfileClient>['user'];
  setActiveTabIndex: Dispatch<SetStateAction<number | undefined>>;
}) => {
  const navigate = useNavigateScroll();
  const { get } = useClient();
  const [searchParams] = useSearchParams();

  const {
    isEditing: editingRoutine,
    setIsEditing: setEditingRoutine,
    panelVisible: routinePanelVisible,
    closePanel: closeRoutinePanel,
    selectedResource: selectedRoutine,
    openPanel: openRoutinePanel,
  } = useResourcePanel<Routine>();
  const [overrideNextRoutine, setOverrideNextRoutine] = useState(false);

  useEffect(() => {
    const routineId = searchParams.get('routineId');
    if (!routinePanelVisible && routineId && organization?.id) {
      // Cambiar a la pestaña de rutinas (índice 1)
      setActiveTabIndex(1);
      // Cargar y abrir el panel con la rutina
      const loadRoutine = async () => {
        const routine = await get<Routine>(`/routines/${routineId}`);
        if (routine) {
          openRoutinePanel(false, routine);
        }
      };
      loadRoutine();
    }
  }, [searchParams, organization?.id, get, openRoutinePanel, routinePanelVisible, setActiveTabIndex]);

  const handleCloseRoutinePanel = () => {
    if (searchParams.get('routineId')) {
      navigate(`/users/profile/${externalUserId}`, { replace: true });
    }
    closeRoutinePanel();
    setOverrideNextRoutine(false);
  };

  const handleViewHistoryRoutine = async (item: RoutineHistoryItem) => {
    if (!organization) return;
    if (item.routineId === user?.currentRoutine?.id) {
      openRoutinePanel(false, user?.currentRoutine);
      return;
    }
    if (item.routineId === user?.currentRoutine?.nextRoutine?.id) {
      openRoutinePanel(false, user?.currentRoutine);
      setOverrideNextRoutine(true);
      return;
    }
    const historyRoutine = await get<Routine>(`/routines/${item.routineId}`);

    openRoutinePanel(false, historyRoutine);
  };

  return {
    editingRoutine,
    setEditingRoutine,
    routinePanelVisible,
    selectedRoutine,
    openRoutinePanel,
    overrideNextRoutine,
    handleCloseRoutinePanel,
    handleViewHistoryRoutine,
  };
};
